import type { WsMessage } from "@/api/websocket";
import {
  bindContextualDiscoveryExecution,
  isContextualDiscoveryPresentationHeld,
} from "@/desktop/onboarding/contextual-discovery-presentation";
import {
  bindCompactPromptScreenshot,
  finishCompactResources,
  publishExecutionResourceFrame,
} from "@/desktop/compact-resource-controller";
import {
  reportRuntimeAuthRequired,
  reportRuntimeAuthRecovered,
} from "@/desktop/connection-health/connection-health-signals";
import { bindPendingOutboundScreens } from "@/desktop/outbound-screenshot-metadata";
import { getWindowRoleSync } from "@/desktop/use-window-role";
import { bindConversationFrameMetadata } from "@/lib/conversation-frame-surface";
import {
  failExecutionFrames,
  finalizeStreamingFrames,
} from "@/lib/frame-execution-patches";
import {
  checkpoint as traceCheckpoint,
  finish as traceFinish,
  rekey as traceRekey,
} from "@/lib/execution-timer";
import { isTauri } from "@/lib/is-tauri";
import {
  localConversationFrameForRequest,
  releaseLocalConversationFrameCorrelation,
} from "@/lib/local-conversation-frame-correlation";
import { preservePresentationOnExecutionStart } from "@/lib/presentation-execution-start";
import type { ExecutionTodo } from "@/stores/companion-store";
import { makeLogEntry, useDebugLogStore } from "@/stores/debug-log-store";
import { applyNativeExecutionStatus } from "./native-execution-status";
import {
  scheduleTerminalCursorRemoval,
  terminalCursorHoldMs,
} from "./terminal-cursor-lifetime";

import type { FrameApplyContext } from "./companion-frame-reducer-shared";
import {
  currentCursorDisplayId,
  getErrorKindToast,
  resolveCursorKeyForExecution,
} from "./companion-frame-reducer-shared";
import { translate } from "@/i18n";
import { libNoticesMessages } from "@/i18n/messages/lib-notices";
import { debugToolsEnabled } from "@/lib/release-channel";
export function applyExecutionEvent(
  msg: WsMessage,
  ctx: FrameApplyContext,
): void {
  if (msg.type === "execution.resource") {
    publishExecutionResourceFrame(msg.data);
    return;
  }
  if (msg.type === "execution.deferred") {
    const dId = msg.data.execution_id as string | undefined;
    const tmplId = msg.data.agent_template_id as string | undefined;
    if (dId && tmplId)
      ctx.canvasStore.getState().markAgentDeferred(dId, tmplId);
    return;
  }
  if (msg.type === "execution.tasks") {
    const { execution_id, todos } = msg.data as {
      execution_id?: string;
      todos: ExecutionTodo[];
    };
    if (execution_id)
      ctx.companionStore.getState().setExecutionTodos(execution_id, todos);
    return;
  }
  if (msg.type === "execution.warning") {
    const warning = (msg.data.warning as string) ?? "";
    if ((msg.data.warning_kind as string) === "tool_rejected" && warning) {
      ctx.addSystemToast({
        id: `warn-${msg.data.execution_id}-${Date.now()}`,
        title: translate(libNoticesMessages, "toolCallRejected"),
        description: warning.slice(0, 300),
        level: "warning",
      });
    }
    if (debugToolsEnabled() && warning) {
      useDebugLogStore.getState().push(
        makeLogEntry("warn", "backend", "execution", warning.slice(0, 500), {
          execution_id: msg.data.execution_id,
          warning_kind: msg.data.warning_kind,
        }),
      );
    }
    return;
  }

  if (applyNativeExecutionStatus(msg, ctx)) return;
  bindContextualDiscoveryExecution(msg.data);
  const execId = msg.data.execution_id as string;
  const status = msg.data.status as string;
  const runtimeSessionId = msg.data.runtime_session_id as string | undefined;
  const conversationId = msg.data.conversation_id as string | undefined;
  const currentProjectId = msg.data.project_id as string | undefined;
  const currentSpaceId =
    currentProjectId ??
    (msg.data.space_id as string | undefined) ??
    ctx.projectStore.getState().currentProject?.id ??
    ctx.projectStore.getState().currentSpace?.id;
  const clientRequestId = msg.data.client_request_id as string | undefined;
  const localConversationFrameId = localConversationFrameForRequest(
    clientRequestId,
    currentSpaceId,
  );
  const conversationFrameId =
    (msg.data.conversation_frame_id as string | undefined) ??
    localConversationFrameId;
  const previousExecution = execId
    ? ctx.companionStore.getState().executions.get(execId)
    : undefined;
  const adapterType =
    (msg.data.adapter_type as string | undefined) ??
    previousExecution?.adapterType;
  const agentName =
    ((msg.data.agent_name as string | undefined) ??
      previousExecution?.agentName) ||
    undefined;
  const agentKeyFromExec = (msg.data.agent_key as string) ?? undefined;
  const originFromMsg = msg.data.origin as "chat" | "heartbeat" | undefined;
  const executionOrigin = originFromMsg ?? previousExecution?.origin ?? "chat";
  const taskIdFromMsg =
    ((msg.data.task_id as string | undefined) ??
      (msg.data.taskId as string | undefined) ??
      previousExecution?.taskId) ||
    undefined;
  const promptText =
    ((msg.data.prompt as string | undefined) ??
      (msg.data.prompt_text as string | undefined)) ||
    undefined;
  const suppressAcknowledgement =
    msg.data.suppress_acknowledgement === true ||
    msg.data.suppressAcknowledgement === true;
  // The cursor map is keyed by agent_id (set in setAgentCursor below).
  // cursorKey must match how cursors are stored, otherwise lookups for
  // freeze / scheduled removal / speakLatestReply silently miss — which
  // was the case until 2026-05-05: completion used session_id while the
  // cursor was stored under agent_id, so TTS never spoke and cursors leaked.
  const cursorKey =
    (msg.data.agent_id as string | undefined) ||
    resolveCursorKeyForExecution(ctx, execId, currentSpaceId ?? undefined) ||
    runtimeSessionId ||
    execId;

  if (status === "queued") {
    ctx.companionStore.getState().addExecution({
      ...previousExecution,
      id: execId,
      status: "queued",
      adapterType,
      runtimeSessionId: runtimeSessionId ?? previousExecution?.runtimeSessionId,
      conversationId: conversationId ?? previousExecution?.conversationId,
      agentName,
      agentKey: agentKeyFromExec ?? previousExecution?.agentKey,
      spaceId: currentSpaceId,
      todos: previousExecution?.todos ?? [],
      origin: executionOrigin,
    });
    return;
  }

  if (status === "running" || status === "waiting_approval") {
    if (msg.device_local !== false) {
      bindCompactPromptScreenshot(clientRequestId, execId);
      bindPendingOutboundScreens({
        clientRequestId,
        executionId: execId,
        sessionId: runtimeSessionId,
      });
    }
    if (status === "running" && msg.device_local !== false) {
      traceRekey("_pending", execId);
      traceCheckpoint(execId, "first_ws_event", true);
      ctx.canvasStore.getState().removePendingPermission(execId);
      ctx.canvasStore.getState().clearAgentDeferredByExecution(execId);
    }
    const { executions } = ctx.companionStore.getState();
    if (executions.has(execId)) {
      ctx.companionStore.getState().updateExecution(execId, {
        status: status as "running" | "waiting_approval",
        adapterType,
        startedAt: previousExecution?.startedAt ?? new Date().toISOString(),
        agentName,
        origin: executionOrigin,
        taskId: taskIdFromMsg,
        runtimeSessionId,
        conversationId,
      });
    } else {
      ctx.companionStore.getState().addExecution({
        id: execId,
        status: status as "running" | "waiting_approval",
        adapterType,
        agentName,
        agentKey: agentKeyFromExec,
        spaceId: currentSpaceId ?? undefined,
        taskId: taskIdFromMsg,
        startedAt: new Date().toISOString(),
        todos: [],
        origin: executionOrigin,
        runtimeSessionId,
        conversationId,
      });
    }
    if (msg.device_local === false) return;
    if (agentKeyFromExec && status === "running") {
      ctx.companionStore
        .getState()
        .setAgentSession(
          agentKeyFromExec,
          currentSpaceId ?? "",
          execId,
          runtimeSessionId,
          conversationId,
          conversationFrameId,
        );
    }
    if (conversationFrameId && ctx.ownsSideEffects !== false) {
      void bindConversationFrameMetadata({
        frameId: conversationFrameId,
        preserveSelectedConversation: true,
        clientRequestId,
        runtimeSessionId,
        conversationId,
        executionId: execId,
        agentKey: agentKeyFromExec,
        agentName,
      });
      if (localConversationFrameId && conversationId) {
        releaseLocalConversationFrameCorrelation(clientRequestId);
      }
    }
    if (status === "running") {
      const cursorColor =
        (msg.data.agent_color as string | undefined) || "var(--color-accent)";
      const preservesPresentation = preservePresentationOnExecutionStart(
        ctx.canvasStore.getState(),
        currentSpaceId,
        cursorKey,
        agentName,
        executionOrigin === "chat",
      );
      if (cursorKey) {
        const pending = ctx.cursorRemovalTimers.get(cursorKey);
        if (pending) {
          clearTimeout(pending);
          ctx.cursorRemovalTimers.delete(cursorKey);
        }
        if (!preservesPresentation) {
          ctx.canvasStore.getState().removeAgentCursor(cursorKey);
          ctx.canvasStore.getState().setAgentCursor({
            agentId: cursorKey,
            spaceId: currentSpaceId ?? "",
            ...(currentProjectId ? { projectId: currentProjectId } : {}),
            executionId: execId,
            ...(runtimeSessionId ? { sessionId: runtimeSessionId } : {}),
            ...(conversationFrameId ? { conversationFrameId } : {}),
            name: agentName ?? "Agent",
            color: cursorColor,
            targetFrameId: null,
            taskId: taskIdFromMsg,
            frozen: false,
            displayId: currentCursorDisplayId(),
            lastSeen: new Date().toISOString(),
            ...(originFromMsg ? { origin: originFromMsg } : {}),
          });
        }
        if (!isContextualDiscoveryPresentationHeld(execId))
          ctx.ttsStore
            .getState()
            .speakAcknowledgement(
              execId,
              executionOrigin,
              suppressAcknowledgement ? "" : promptText,
            );
      }
    }
    return;
  }

  // Terminal status
  finishCompactResources(execId);
  const errorMessage =
    status === "failed" ? ((msg.data.error as string) ?? undefined) : undefined;
  ctx.companionStore.getState().updateExecution(execId, {
    status: status as "completed" | "failed" | "cancelled" | "paused",
    errorMessage,
    origin: executionOrigin,
    taskId: taskIdFromMsg,
  });
  if (msg.device_local === false) return;
  if (
    status === "completed" &&
    adapterType &&
    previousExecution?.startedAt &&
    isTauri() &&
    getWindowRoleSync() === "owner"
  )
    reportRuntimeAuthRecovered(
      adapterType,
      Date.parse(previousExecution.startedAt),
    );
  if (status === "failed" || status === "cancelled") {
    ctx.ttsStore.getState().cancelAcknowledgement(execId);
  }
  if (status !== "completed")
    ctx.companionStore.getState().clearSessionByExecution(execId);
  ctx.canvasStore.getState().removePendingPermission(execId);
  ctx.canvasStore.getState().clearAgentDeferredByExecution(execId);

  const freezeCursor = () => {
    const c = ctx.canvasStore.getState().agentCursors.get(cursorKey);
    if (!c || c.frozen) return;
    ctx.canvasStore.getState().setAgentCursor({
      agentId: c.agentId,
      spaceId: c.spaceId,
      name: c.name,
      color: c.color,
      targetFrameId: c.targetFrameId,
      taskId: c.taskId,
      frozen: true,
      displayId: currentCursorDisplayId(),
      lastSeen: new Date().toISOString(),
    });
  };

  const clearStreamingDraftSoon = (delayMs: number) => {
    ctx.defer(() => {
      ctx.conversationStore.getState().clearStreamingAssistantMessage(execId);
    }, delayMs);
  };

  if (status === "completed") {
    finalizeStreamingFrames();
    traceCheckpoint(execId, "execution_completed");
    traceFinish(execId);
    ctx.companionStore.getState().turnCompleted(execId);
    const ttsOrigin =
      ctx.companionStore.getState().executions.get(execId)?.origin ===
      "heartbeat"
        ? "heartbeat"
        : "chat";
    if (!isContextualDiscoveryPresentationHeld(execId))
      ctx.ttsStore
        .getState()
        .speakLatestReply(cursorKey, execId, ttsOrigin, agentName);
    clearStreamingDraftSoon(2_500);
    freezeCursor();
    const terminalCursor = ctx.canvasStore
      .getState()
      .agentCursors.get(cursorKey);
    scheduleTerminalCursorRemoval(
      ctx,
      cursorKey,
      execId,
      terminalCursorHoldMs(
        terminalCursor?.contentFull ?? terminalCursor?.content,
      ),
    );
  } else if (status === "failed") {
    const errMsg = (errorMessage ?? "").toLowerCase();
    if (debugToolsEnabled() && errorMessage) {
      useDebugLogStore.getState().push(
        makeLogEntry("error", "backend", "execution", errorMessage, {
          execution_id: execId,
          error_kind: msg.data.error_kind,
        }),
      );
    }
    const isTransient =
      errMsg.includes("companion restarting") ||
      errMsg.includes("companion disconnected");
    if (isTransient) {
      finalizeStreamingFrames();
      traceCheckpoint(execId, "execution_completed");
      traceFinish(execId);
      ctx.companionStore.getState().turnCompleted(execId);
      ctx.companionStore.getState().setCurrentExecution(null);
      ctx.conversationStore.getState().clearStreamingAssistantMessage(execId);
      freezeCursor();
      scheduleTerminalCursorRemoval(ctx, cursorKey, execId, 5_000);
    } else {
      failExecutionFrames(execId);
      traceCheckpoint(execId, "execution_completed");
      traceFinish(execId);
      ctx.companionStore.getState().turnCompleted(execId);
      ctx.companionStore.getState().setCurrentExecution(null);
      ctx.conversationStore.getState().clearStreamingAssistantMessage(execId);
      const ec = ctx.canvasStore.getState().agentCursors.get(cursorKey);
      if (ec)
        ctx.canvasStore.getState().setAgentCursor({
          agentId: ec.agentId,
          spaceId: ec.spaceId,
          name: ec.name,
          color: ec.color,
          targetFrameId: ec.targetFrameId,
          taskId: ec.taskId,
          error: (
            errorMessage ?? translate(libNoticesMessages, "agentStopped")
          ).slice(0, 120),
          displayId: currentCursorDisplayId(),
          lastSeen: new Date().toISOString(),
        });
      const errorKind = (msg.data.error_kind as string) ?? "";
      if (isTauri() && errorKind === "auth_required") {
        // The owner's connection-health host offers a guided sign-in instead.
        if (getWindowRoleSync() === "owner")
          reportRuntimeAuthRequired(adapterType);
      } else {
        const toast = getErrorKindToast(errorKind, errMsg, execId);
        if (toast) ctx.addSystemToast(toast);
      }
      freezeCursor();
      scheduleTerminalCursorRemoval(ctx, cursorKey, execId, 15_000);
    }
  } else if (status === "cancelled") {
    finalizeStreamingFrames();
    traceCheckpoint(execId, "execution_completed");
    traceFinish(execId);
    ctx.companionStore.getState().turnCompleted(execId);
    ctx.companionStore.getState().setCurrentExecution(null);
    ctx.conversationStore.getState().clearStreamingAssistantMessage(execId);
    freezeCursor();
    scheduleTerminalCursorRemoval(ctx, cursorKey, execId, 3_000);
  } else if (status === "paused") {
    finalizeStreamingFrames();
    traceCheckpoint(execId, "execution_paused");
    traceFinish(execId);
    ctx.companionStore.getState().turnCompleted(execId);
    // Speak the agent's reply on pause too — the agent has emitted content
    // and yielded control. The user expects to hear it whether the agent
    // is fully done or just awaiting more input. Mirror the `completed`
    // branch's TTS dispatch.
    const ttsOrigin =
      ctx.companionStore.getState().executions.get(execId)?.origin ===
      "heartbeat"
        ? "heartbeat"
        : "chat";
    if (!isContextualDiscoveryPresentationHeld(execId))
      ctx.ttsStore
        .getState()
        .speakLatestReply(cursorKey, execId, ttsOrigin, agentName);
    clearStreamingDraftSoon(2_500);
    freezeCursor();
    scheduleTerminalCursorRemoval(ctx, cursorKey, execId, 3_000);
  }
}

/** Tags one question's speech so answering it silences that question only. */
